// Browser-side copy of the customer-detail tidy-up rules.
//
// The canonical rules live in base44/shared/customerFormat.ts, but the browser
// cannot import from base44/ (server-side only). This mirror is used by the
// capture points that write straight through the platform SDK — registration
// and the MY DETAILS profile form — so a value is tidy the moment it is saved.
// Keep the two files in step.

// Trim and collapse runs of whitespace.
export function tidyText(value) {
  return String(value ?? '').replace(/\s+/g, ' ').trim();
}

// Capitalise the first letter of a word and the letter following an internal
// hyphen or apostrophe, leaving the rest of the word untouched.
function capitaliseWord(word) {
  return word.replace(/(^|[-'’])(\p{L})/gu, (_m, sep, ch) => sep + ch.toUpperCase());
}

// Person names: "tom mallon" and "TOM MALLON" both become "Tom Mallon". A name
// that already mixes case ("McDonald", "van der Berg") is left as typed so an
// intentional capital is never flattened. Words containing digits are left alone.
export function tidyName(value) {
  const s = tidyText(value);
  if (!s) return '';
  const source = /[a-z]/.test(s) ? s : s.toLowerCase();
  return source.replace(/\S+/g, (w) => (/\d/.test(w) ? w : capitaliseWord(w)));
}

// UK postcode: letters, digit, optional letter/digit, then digit + two letters.
const UK_POSTCODE = /^([A-Z]{1,2}\d[A-Z\d]?)(\d[A-Z]{2})$/;

// Postcodes are always upper case; a UK postcode also gets the standard single
// space before the final three characters ("ch623ra" → "CH62 3RA").
export function tidyPostcode(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  const compact = raw.replace(/\s+/g, '').toUpperCase();
  const m = compact.match(UK_POSTCODE);
  if (m) return `${m[1]} ${m[2]}`;
  return raw.replace(/\s+/g, ' ').toUpperCase();
}

export function tidyPhone(value) {
  return String(value ?? '').trim();
}

// Tidy a profile / registration form's name, phone and address fields.
export function tidyProfile(form) {
  return {
    first_name: tidyName(form?.first_name),
    last_name: tidyName(form?.last_name),
    phone: tidyPhone(form?.phone),
    address_line1: tidyText(form?.address_line1),
    address_line2: tidyText(form?.address_line2),
    city: tidyText(form?.city),
    postcode: tidyPostcode(form?.postcode),
  };
}